import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Navigation from "@/components/Navigation";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Instagram,
  Facebook,
  Youtube,
  Globe,
  Users,
  Target,
  Award,
  FileText,
  Info,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Camera,
  BookOpen,
  Pencil,
  Plus,
  X,
  Loader2,
} from "lucide-react";
import { SiTiktok, SiPinterest } from "react-icons/si";

// Define the validation schema
const influencerApplicationSchema = z.object({
  // Personal Information
  fullName: z.string().min(2, "Full name is required"),
  email: z.string().email("Valid email is required"),
  phoneNumber: z.string().min(10, "Valid phone number is required"),
  location: z.object({
    city: z.string().min(2, "City is required"),
    stateOrCountry: z.string().min(2, "State/Country is required"),
  }),
  bio: z.string().min(50, "Please provide at least 50 characters for your bio").max(500, "Bio must be less than 500 characters"),
  
  // Social Media Information
  socialMedia: z.object({
    instagram: z.object({
      handle: z.string().optional(),
      followers: z.number().min(0).optional(),
    }),
    facebook: z.object({
      url: z.string().url().optional().or(z.literal("")),
      followers: z.number().min(0).optional(),
    }),
    tiktok: z.object({
      username: z.string().optional(),
      followers: z.number().min(0).optional(),
    }),
    youtube: z.object({
      url: z.string().url().optional().or(z.literal("")),
      subscribers: z.number().min(0).optional(),
    }),
    pinterest: z.object({
      profile: z.string().optional(),
      followers: z.number().min(0).optional(),
    }),
    blog: z.object({
      url: z.string().url().optional().or(z.literal("")),
      monthlyVisitors: z.number().min(0).optional(),
    }),
    otherPlatforms: z.string().optional(),
  }),
  
  // Audience Demographics
  audienceDemographics: z.object({
    ageRanges: z.array(z.string()).min(1, "Select at least one age range"),
    interests: z.array(z.string()).min(1, "Select at least one interest"),
    geographicReach: z.array(z.string()).min(1, "Select at least one geographic reach"),
  }),
  
  // Content & Experience
  contentExperience: z.object({
    focusAreas: z.array(z.string()).min(1, "Select at least one content focus area"),
    previousCollaborations: z.string().max(1000, "Please keep under 1000 characters"),
    partnershipReason: z.string().min(50, "Please provide at least 50 characters").max(1000, "Please keep under 1000 characters"),
    portfolioLinks: z.array(z.string().url().optional()).optional(),
  }),
  
  // Agreement Terms
  agreements: z.object({
    commissionBased: z.boolean().refine((val) => val === true, "You must agree to commission-based partnership"),
    brandGuidelines: z.boolean().refine((val) => val === true, "You must agree to brand guidelines"),
    ftcDisclosure: z.boolean().refine((val) => val === true, "You must agree to FTC disclosure requirements"),
  }),
});

type InfluencerApplicationFormData = z.infer<typeof influencerApplicationSchema>;

const AGE_RANGES = [
  { value: "0-2", label: "0-2 years" },
  { value: "2-4", label: "2-4 years" },
  { value: "4-6", label: "4-6 years" },
  { value: "6-8", label: "6-8 years" },
  { value: "8+", label: "8+ years" },
];

const INTERESTS = [
  { value: "parenting", label: "Parenting" },
  { value: "education", label: "Education" },
  { value: "toys", label: "Toys" },
  { value: "books", label: "Books" },
  { value: "crafts", label: "Arts & Crafts" },
  { value: "outdoor", label: "Outdoor Activities" },
  { value: "stem", label: "STEM" },
  { value: "storytelling", label: "Storytelling" },
  { value: "family", label: "Family Activities" },
  { value: "wellness", label: "Child Wellness" },
];

const GEOGRAPHIC_REACH = [
  { value: "local", label: "Local (City/State)" },
  { value: "national", label: "National" },
  { value: "international", label: "International" },
];

const CONTENT_FOCUS_AREAS = [
  { value: "toy_reviews", label: "Toy Reviews" },
  { value: "book_recommendations", label: "Book Recommendations" },
  { value: "parenting_tips", label: "Parenting Tips" },
  { value: "educational_content", label: "Educational Content" },
  { value: "product_unboxing", label: "Product Unboxing" },
  { value: "family_vlogs", label: "Family Vlogs" },
  { value: "crafts_diy", label: "Crafts & DIY" },
  { value: "storytelling", label: "Story Reading/Telling" },
];

export default function InfluencerApplication() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [portfolioLinks, setPortfolioLinks] = useState<string[]>([""]);
  const totalSteps = 5;

  // Check eligibility
  const { data: eligibility, isLoading: isCheckingEligibility, error: eligibilityError } = useQuery<{
    eligible: boolean;
    reason?: string;
    isInfluencer?: boolean;
    hasApplication?: boolean;
  }>({
    queryKey: ['/api/influencer/check-eligibility'],
    enabled: !!user,
  });

  // Initialize form with react-hook-form and zod
  const form = useForm<InfluencerApplicationFormData>({
    resolver: zodResolver(influencerApplicationSchema),
    defaultValues: {
      fullName: user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "",
      email: user?.email || "",
      phoneNumber: "",
      location: {
        city: "",
        stateOrCountry: "",
      },
      bio: "",
      socialMedia: {
        instagram: { handle: "", followers: undefined },
        facebook: { url: "", followers: undefined },
        tiktok: { username: "", followers: undefined },
        youtube: { url: "", subscribers: undefined },
        pinterest: { profile: "", followers: undefined },
        blog: { url: "", monthlyVisitors: undefined },
        otherPlatforms: "",
      },
      audienceDemographics: {
        ageRanges: [],
        interests: [],
        geographicReach: [],
      },
      contentExperience: {
        focusAreas: [],
        previousCollaborations: "",
        partnershipReason: "",
        portfolioLinks: [],
      },
      agreements: {
        commissionBased: false,
        brandGuidelines: false,
        ftcDisclosure: false,
      },
    },
  });

  // Submit application mutation
  const submitApplicationMutation = useMutation({
    mutationFn: async (data: InfluencerApplicationFormData) => {
      return await apiRequest("POST", "/api/influencer/apply", data);
    },
    onSuccess: () => {
      toast({
        title: "Application Submitted!",
        description: "Your influencer application has been received. We'll review it and get back to you soon.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      // Redirect to influencer dashboard
      navigate("/influencer");
    },
    onError: (error: any) => {
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit your application. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Handle step navigation
  const handleNext = async () => {
    // Validate current step fields
    let fieldsToValidate: any[] = [];
    
    switch (currentStep) {
      case 1: // Personal Information
        fieldsToValidate = ["fullName", "email", "phoneNumber", "location", "bio"];
        break;
      case 2: // Social Media
        // Social media fields are optional, so we can proceed
        break;
      case 3: // Audience Demographics
        fieldsToValidate = ["audienceDemographics"];
        break;
      case 4: // Content & Experience
        fieldsToValidate = ["contentExperience"];
        break;
      case 5: // Agreements
        fieldsToValidate = ["agreements"];
        break;
    }

    if (fieldsToValidate.length > 0) {
      const isValid = await form.trigger(fieldsToValidate as any);
      if (!isValid) {
        toast({
          title: "Please complete required fields",
          description: "Fill in all required fields before proceeding.",
          variant: "destructive",
        });
        return;
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = form.handleSubmit((data) => {
    // Add portfolio links to the data
    data.contentExperience.portfolioLinks = portfolioLinks.filter(link => link.trim() !== "");
    submitApplicationMutation.mutate(data);
  });

  // Handle portfolio links
  const addPortfolioLink = () => {
    setPortfolioLinks([...portfolioLinks, ""]);
  };

  const removePortfolioLink = (index: number) => {
    setPortfolioLinks(portfolioLinks.filter((_, i) => i !== index));
  };

  const updatePortfolioLink = (index: number, value: string) => {
    const updated = [...portfolioLinks];
    updated[index] = value;
    setPortfolioLinks(updated);
  };

  // Show loading state
  if (isCheckingEligibility) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <CardContent className="p-8">
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <p className="text-gray-600">Checking your eligibility...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Show error if not eligible
  if (eligibilityError || (eligibility && !eligibility.eligible)) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-yellow-600" />
                Not Eligible
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {eligibility?.reason || "You are not currently eligible to apply for the influencer program. Please contact support for more information."}
                </AlertDescription>
              </Alert>
              <Button onClick={() => navigate("/home")} className="w-full">
                Return to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const progress = (currentStep / totalSteps) * 100;

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent mb-2">
              SnuggleCrafters Influencer Program Application
            </h1>
            <p className="text-gray-600">
              Join our community of content creators and earn commissions while sharing magical stories
            </p>
          </div>

          {/* Progress Bar */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Step {currentStep} of {totalSteps}</span>
                  <span>{Math.round(progress)}% Complete</span>
                </div>
                <Progress value={progress} className="h-2" />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>Personal Info</span>
                  <span>Social Media</span>
                  <span>Audience</span>
                  <span>Experience</span>
                  <span>Agreement</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form */}
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-purple-600" />
                      Personal Information
                    </CardTitle>
                    <CardDescription>
                      Tell us about yourself so we can get to know you better
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="fullName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name *</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                placeholder="Jane Doe"
                                data-testid="input-full-name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address *</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                type="email" 
                                placeholder="jane@example.com"
                                data-testid="input-email"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number *</FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              type="tel" 
                              placeholder="+1 (555) 123-4567"
                              data-testid="input-phone"
                            />
                          </FormControl>
                          <FormDescription>
                            We'll only use this for important communications
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="location.city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>City *</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                placeholder="San Francisco"
                                data-testid="input-city"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="location.stateOrCountry"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>State/Country *</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                placeholder="California, USA"
                                data-testid="input-state-country"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="bio"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bio / Introduction *</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field} 
                              placeholder="Tell us about yourself, your content, and your audience..."
                              className="min-h-[120px]"
                              data-testid="textarea-bio"
                            />
                          </FormControl>
                          <FormDescription className="flex justify-between">
                            <span>Briefly introduce yourself and your content</span>
                            <span className="text-xs">{field.value?.length || 0}/500</span>
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Step 2: Social Media Information */}
              {currentStep === 2 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Camera className="h-5 w-5 text-purple-600" />
                      Social Media Presence
                    </CardTitle>
                    <CardDescription>
                      Share your social media profiles and follower counts
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Instagram */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Instagram className="h-4 w-4" />
                        Instagram
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <FormField
                          control={form.control}
                          name="socialMedia.instagram.handle"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Handle</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="@username"
                                  data-testid="input-instagram-handle"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="socialMedia.instagram.followers"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Followers</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number"
                                  placeholder="10000"
                                  value={field.value || ""}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                  data-testid="input-instagram-followers"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Facebook */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Facebook className="h-4 w-4" />
                        Facebook
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <FormField
                          control={form.control}
                          name="socialMedia.facebook.url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Page URL</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="https://facebook.com/yourpage"
                                  data-testid="input-facebook-url"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="socialMedia.facebook.followers"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Followers</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number"
                                  placeholder="5000"
                                  value={field.value || ""}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                  data-testid="input-facebook-followers"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* TikTok */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <SiTiktok className="h-4 w-4" />
                        TikTok
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <FormField
                          control={form.control}
                          name="socialMedia.tiktok.username"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Username</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="@username"
                                  data-testid="input-tiktok-username"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="socialMedia.tiktok.followers"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Followers</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number"
                                  placeholder="15000"
                                  value={field.value || ""}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                  data-testid="input-tiktok-followers"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* YouTube */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Youtube className="h-4 w-4" />
                        YouTube
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <FormField
                          control={form.control}
                          name="socialMedia.youtube.url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Channel URL</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="https://youtube.com/@channel"
                                  data-testid="input-youtube-url"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="socialMedia.youtube.subscribers"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Subscribers</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number"
                                  placeholder="8000"
                                  value={field.value || ""}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                  data-testid="input-youtube-subscribers"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Pinterest */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <SiPinterest className="h-4 w-4" />
                        Pinterest
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <FormField
                          control={form.control}
                          name="socialMedia.pinterest.profile"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Profile</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="pinterest.com/username"
                                  data-testid="input-pinterest-profile"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="socialMedia.pinterest.followers"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Followers</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number"
                                  placeholder="3000"
                                  value={field.value || ""}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                  data-testid="input-pinterest-followers"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Blog/Website */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Globe className="h-4 w-4" />
                        Blog/Website (Optional)
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <FormField
                          control={form.control}
                          name="socialMedia.blog.url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>URL</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="https://yourblog.com"
                                  data-testid="input-blog-url"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="socialMedia.blog.monthlyVisitors"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Monthly Visitors</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number"
                                  placeholder="10000"
                                  value={field.value || ""}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                  data-testid="input-blog-visitors"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Other Platforms */}
                    <FormField
                      control={form.control}
                      name="socialMedia.otherPlatforms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Other Platforms (Optional)</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field} 
                              placeholder="List any other platforms where you have a presence (e.g., Threads, LinkedIn, etc.)"
                              className="min-h-[80px]"
                              data-testid="textarea-other-platforms"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Step 3: Audience Demographics */}
              {currentStep === 3 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-600" />
                      Audience Demographics
                    </CardTitle>
                    <CardDescription>
                      Help us understand your audience better
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Age Ranges */}
                    <FormField
                      control={form.control}
                      name="audienceDemographics.ageRanges"
                      render={() => (
                        <FormItem>
                          <div className="mb-4">
                            <FormLabel>Primary Audience Age Range *</FormLabel>
                            <FormDescription>
                              Select all age groups that make up your primary audience
                            </FormDescription>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {AGE_RANGES.map((ageRange) => (
                              <FormField
                                key={ageRange.value}
                                control={form.control}
                                name="audienceDemographics.ageRanges"
                                render={({ field }) => {
                                  return (
                                    <FormItem
                                      key={ageRange.value}
                                      className="flex flex-row items-start space-x-3 space-y-0"
                                    >
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(ageRange.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, ageRange.value])
                                              : field.onChange(
                                                  field.value?.filter(
                                                    (value) => value !== ageRange.value
                                                  )
                                                );
                                          }}
                                          data-testid={`checkbox-age-${ageRange.value}`}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal cursor-pointer">
                                        {ageRange.label}
                                      </FormLabel>
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />

                    {/* Interests */}
                    <FormField
                      control={form.control}
                      name="audienceDemographics.interests"
                      render={() => (
                        <FormItem>
                          <div className="mb-4">
                            <FormLabel>Audience Interests *</FormLabel>
                            <FormDescription>
                              What topics is your audience interested in?
                            </FormDescription>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {INTERESTS.map((interest) => (
                              <FormField
                                key={interest.value}
                                control={form.control}
                                name="audienceDemographics.interests"
                                render={({ field }) => {
                                  return (
                                    <FormItem
                                      key={interest.value}
                                      className="flex flex-row items-start space-x-3 space-y-0"
                                    >
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(interest.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, interest.value])
                                              : field.onChange(
                                                  field.value?.filter(
                                                    (value) => value !== interest.value
                                                  )
                                                );
                                          }}
                                          data-testid={`checkbox-interest-${interest.value}`}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal cursor-pointer">
                                        {interest.label}
                                      </FormLabel>
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />

                    {/* Geographic Reach */}
                    <FormField
                      control={form.control}
                      name="audienceDemographics.geographicReach"
                      render={() => (
                        <FormItem>
                          <div className="mb-4">
                            <FormLabel>Geographic Reach *</FormLabel>
                            <FormDescription>
                              Where is your audience located?
                            </FormDescription>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {GEOGRAPHIC_REACH.map((reach) => (
                              <FormField
                                key={reach.value}
                                control={form.control}
                                name="audienceDemographics.geographicReach"
                                render={({ field }) => {
                                  return (
                                    <FormItem
                                      key={reach.value}
                                      className="flex flex-row items-start space-x-3 space-y-0"
                                    >
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(reach.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, reach.value])
                                              : field.onChange(
                                                  field.value?.filter(
                                                    (value) => value !== reach.value
                                                  )
                                                );
                                          }}
                                          data-testid={`checkbox-reach-${reach.value}`}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal cursor-pointer">
                                        {reach.label}
                                      </FormLabel>
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Step 4: Content & Experience */}
              {currentStep === 4 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="h-5 w-5 text-purple-600" />
                      Content & Experience
                    </CardTitle>
                    <CardDescription>
                      Tell us about your content creation experience
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Content Focus Areas */}
                    <FormField
                      control={form.control}
                      name="contentExperience.focusAreas"
                      render={() => (
                        <FormItem>
                          <div className="mb-4">
                            <FormLabel>Content Focus Areas *</FormLabel>
                            <FormDescription>
                              What type of content do you typically create?
                            </FormDescription>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {CONTENT_FOCUS_AREAS.map((area) => (
                              <FormField
                                key={area.value}
                                control={form.control}
                                name="contentExperience.focusAreas"
                                render={({ field }) => {
                                  return (
                                    <FormItem
                                      key={area.value}
                                      className="flex flex-row items-start space-x-3 space-y-0"
                                    >
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(area.value)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, area.value])
                                              : field.onChange(
                                                  field.value?.filter(
                                                    (value) => value !== area.value
                                                  )
                                                );
                                          }}
                                          data-testid={`checkbox-focus-${area.value}`}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal cursor-pointer">
                                        {area.label}
                                      </FormLabel>
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />

                    {/* Previous Collaborations */}
                    <FormField
                      control={form.control}
                      name="contentExperience.previousCollaborations"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Previous Brand Collaborations</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field} 
                              placeholder="Tell us about any previous brand partnerships or collaborations you've had..."
                              className="min-h-[100px]"
                              data-testid="textarea-collaborations"
                            />
                          </FormControl>
                          <FormDescription className="flex justify-between">
                            <span>List any brands you've worked with</span>
                            <span className="text-xs">{field.value?.length || 0}/1000</span>
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Why Partner */}
                    <FormField
                      control={form.control}
                      name="contentExperience.partnershipReason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Why SnuggleCrafters? *</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field} 
                              placeholder="Tell us why you want to partner with SnuggleCrafters and how you would promote our personalized story platform..."
                              className="min-h-[120px]"
                              data-testid="textarea-why-partner"
                            />
                          </FormControl>
                          <FormDescription className="flex justify-between">
                            <span>Share your vision for this partnership</span>
                            <span className="text-xs">{field.value?.length || 0}/1000</span>
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />

                    {/* Portfolio Links */}
                    <div className="space-y-4">
                      <div>
                        <FormLabel>Content Samples / Portfolio Links</FormLabel>
                        <FormDescription>
                          Share links to your best content examples
                        </FormDescription>
                      </div>
                      {portfolioLinks.map((link, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={link}
                            onChange={(e) => updatePortfolioLink(index, e.target.value)}
                            placeholder="https://example.com/your-content"
                            data-testid={`input-portfolio-${index}`}
                          />
                          {portfolioLinks.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => removePortfolioLink(index)}
                              data-testid={`button-remove-portfolio-${index}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {portfolioLinks.length < 5 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addPortfolioLink}
                          className="mt-2"
                          data-testid="button-add-portfolio"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Another Link
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Step 5: Agreement Terms */}
              {currentStep === 5 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600" />
                      Partnership Agreement
                    </CardTitle>
                    <CardDescription>
                      Please review and agree to our partnership terms
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Please carefully review each agreement below. You must agree to all terms to proceed with your application.
                      </AlertDescription>
                    </Alert>

                    {/* Commission Agreement */}
                    <FormField
                      control={form.control}
                      name="agreements.commissionBased"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-4">
                          <FormControl>
                            <Checkbox 
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="checkbox-commission"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>
                              Commission-Based Partnership Agreement *
                            </FormLabel>
                            <FormDescription>
                              I agree to participate in a commission-based partnership program where I will earn 10% commission on sales generated through my unique referral code. I understand that commissions are paid monthly via Stripe Connect.
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />

                    {/* Brand Guidelines */}
                    <FormField
                      control={form.control}
                      name="agreements.brandGuidelines"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-4">
                          <FormControl>
                            <Checkbox 
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="checkbox-guidelines"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>
                              Brand Guidelines Agreement *
                            </FormLabel>
                            <FormDescription>
                              I agree to follow SnuggleCrafters' brand guidelines when creating content. This includes using approved messaging, maintaining brand voice, and representing the product accurately and honestly.
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />

                    {/* FTC Disclosure */}
                    <FormField
                      control={form.control}
                      name="agreements.ftcDisclosure"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-4">
                          <FormControl>
                            <Checkbox 
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="checkbox-ftc"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>
                              FTC Disclosure Requirements *
                            </FormLabel>
                            <FormDescription>
                              I agree to comply with FTC disclosure requirements by clearly disclosing my partnership with SnuggleCrafters in all sponsored content using appropriate hashtags (#ad, #sponsored, #partner) and verbal/written disclosures as required by law.
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormMessage />

                    {/* Additional Information */}
                    <Alert className="bg-purple-50 border-purple-200">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      <AlertDescription className="text-purple-900">
                        <strong>What happens next?</strong>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                          <li>We'll review your application within 3-5 business days</li>
                          <li>If approved, you'll receive an email with next steps</li>
                          <li>You'll get access to your influencer dashboard and unique referral code</li>
                          <li>We'll provide you with media assets and brand guidelines</li>
                        </ul>
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  data-testid="button-previous"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>

                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    data-testid="button-next"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={submitApplicationMutation.isPending}
                    className="bg-gradient-to-r from-purple-600 to-teal-600"
                    data-testid="button-submit"
                  >
                    {submitApplicationMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
}